import {
  Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, input
} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import '@geodan/gm-map';
import {fromEvent, take} from 'rxjs';
import {Feature, Map as OlMap, View} from 'ol';
import {addLayer} from '@geodan/gm-map-functions/build/dist/ol-map-functions';
import {DragPan} from 'ol/interaction';
import {Fill, Stroke, Style} from 'ol/style';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import {Geometry} from 'ol/geom';
import {ResourcesService} from '../../services/resources.service';
import {Question} from '../../models/game-data.model';
import {QuestionState} from '../../models/game-models.model';

@Component({
  selector: 'gis-question-answer-card',
  imports: [NgTemplateOutlet],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './question-answer-card.component.html',
  styleUrl: './question-answer-card.component.scss'
})
export class QuestionAnswerCardComponent {
  protected readonly resourcesService = inject(ResourcesService);
  private readonly elRef = inject(ElementRef);

  readonly question = input.required<Question>();
  readonly questionState = input<QuestionState>();

  readonly mapConfig = computed(() => JSON.stringify(this.resourcesService.mapConfig.value()));

  isExpanded = false;
  map?: OlMap;
  baseLayer?: VectorLayer<VectorSource<Feature<Geometry>>>;
  addLayer = addLayer;
  protected readonly Array = Array;

  constructor() {
    this.addBorderLayer = this.addBorderLayer.bind(this);
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      fromEvent(this.elRef.nativeElement, 'gm-map-created').pipe(take(1))
        .subscribe(event => {
          this.setMap(event);
          const layer = this.addLayer(this.map, this.question().answerLayerId);
          layer.setVisible(true);
        });
    }
  }

  setMap(mapCreatedEvent: unknown) {
    this.map = (mapCreatedEvent as Event & { detail: { map: OlMap } }).detail.map;
    this.setMapView();
    this.addBaseLayer();
    this.map?.getInteractions().clear();
    this.map?.addInteraction(new DragPan());
  }

  setMapView() {
    const xMin = -68000;
    const yMin = 296000;
    const xMax = 359000;
    const yMax = 630000;
    this.map?.setView(new View({
      center: [(xMin + xMax) / 2, (yMin + yMax) / 2],
      extent: [xMin, yMin, xMax, yMax],
      projection: 'EPSG:28992',
      zoom: 3,
      minZoom: 1,
      maxZoom: 14,
      showFullExtent: true
    }));
  }

  addBaseLayer() {
    const grayStyle = new Style({
      fill: new Fill({color: '#e5e5e5'}),
      stroke: new Stroke({color: 'white'})
    });

    const layerId = this.question().gameSetting.gameLayer;
    this.baseLayer = this.addLayer(this.map, layerId) as VectorLayer<VectorSource<Feature<Geometry>>>;
    this.baseLayer.setStyle(grayStyle);
    this.baseLayer.setVisible(true);
    this.baseLayer.on('postrender', this.addBorderLayer);
  }

  addBorderLayer() {
    const borderStyle = new Style({
      fill: undefined,
      stroke: new Stroke({color: 'white', width: 2})
    });

    const features = this.baseLayer?.getSource()?.getFeatures().map(f => f.clone()) ?? [];

    if (features.length) {
      features.forEach(feature => {
        feature.setStyle(borderStyle);
        feature.set('isQuestionBorder', true);
      });

      this.map?.addLayer(new VectorLayer({
        source: new VectorSource({features}),
        zIndex: Infinity,
      }));
      this.baseLayer?.un('postrender', this.addBorderLayer);
      this.fitToCorrectFeature();
    }
  }

  private fitToCorrectFeature() {
    const {featureIdKey, featureIdCorrectValue} = this.question().gameSetting;
    const correctFeature = this.baseLayer?.getSource()?.getFeatures()
      .find(f => f.get(featureIdKey) === featureIdCorrectValue);
    const geometry = correctFeature?.getGeometry();
    if (geometry) {
      this.map?.getView().fit(geometry.getExtent(), {padding: [20, 20, 20, 20]});
    }
  }
}
